import '@/App.css';
import '@/native.css';
import '@/a11y.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Marketplace from './pages/Marketplace';
import MovieDetail from './pages/MovieDetail';
import InvestorDashboard from './pages/InvestorDashboard';
import ProducerDashboard from './pages/ProducerDashboard';
import FanDashboard from './pages/FanDashboard';
import Community from './pages/Community';
import CommunityPostDetail from './pages/CommunityPostDetail';
import AdminDashboard from './pages/AdminDashboard';
import PaymentSuccess from './pages/PaymentSuccess';
import ProducerTrackRecord from './pages/ProducerTrackRecord';
import ProducerProfilePublic from './pages/ProducerProfilePublic';
import MovieCrewManage from './pages/MovieCrewManage';
import FundingSetup from './pages/FundingSetup';
// ZingyWallet import kept out — the module is parked pending regulatory review.
// To re-enable, restore this import and the /investor/wallet <Route> below,
// and flip ZINGY_ENABLED=true in backend/.env. See /app/memory/ZINGY_MODULE_PARKED.md.
import Fraternity from './pages/Fraternity';
import FraternityProfile from './pages/FraternityProfile';
import FraternityProfileEdit from './pages/FraternityProfileEdit';
import FraternityInbox from './pages/FraternityInbox';
import Legal from './pages/Legal';
import InviteFriends from './pages/InviteFriends';
import Notifications from './pages/Notifications';
import VisitorDisclaimerBanner from './components/VisitorDisclaimerBanner';
import InstallAppBanner from './components/InstallAppBanner';
import ThemesIndex from './pages/themes/ThemesIndex';
import ThemeBollywood from './pages/themes/ThemeBollywood';
import ThemeHotstar from './pages/themes/ThemeHotstar';
import ThemeFestival from './pages/themes/ThemeFestival';
import { Toaster } from 'sonner';
import { MotionConfig } from 'framer-motion';
import NativeAppShell from './components/native/NativeAppShell';
import { isNativeApp } from './lib/native';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      setUser(JSON.parse(userData));
    }
    setLoading(false);
  }, []);  // run once on mount; state setters are stable refs

  const ProtectedRoute = ({ children, allowedRoles }) => {
    if (loading) return null;
    if (!user) return <Navigate to="/login" />;
    if (allowedRoles && !allowedRoles.includes(user.role)) {
      return <Navigate to="/marketplace" />;
    }
    return children;
  };

  if (loading) return null;

  return (
    <MotionConfig reducedMotion="user">
    <div className="App">
      <BrowserRouter>
        <NativeAppShell user={user} setUser={setUser}>
        <Routes>
          <Route path="/" element={<ThemeHotstar user={user} />} />
          <Route path="/classic" element={<Landing user={user} />} />
          <Route path="/login" element={<Login setUser={setUser} />} />
          <Route path="/register" element={<Register setUser={setUser} />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/marketplace" element={<Marketplace user={user} />} />
          <Route path="/movie/:id" element={<MovieDetail user={user} />} />
          <Route
            path="/investor-dashboard"
            element={
              <ProtectedRoute allowedRoles={['investor', 'admin']}>
                <InvestorDashboard user={user} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/producer-dashboard"
            element={
              <ProtectedRoute allowedRoles={['producer', 'admin']}>
                <ProducerDashboard user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/fan-dashboard"
            element={
              <ProtectedRoute allowedRoles={['fan', 'admin']}>
                <FanDashboard user={user} />
              </ProtectedRoute>
            }
          />
          <Route path="/community" element={<Community user={user} />} />
          <Route path="/community/post/:id" element={<CommunityPostDetail user={user} />} />
          <Route path="/payment/success" element={<PaymentSuccess user={user} />} />
          <Route
            path="/producer/track-record"
            element={
              <ProtectedRoute allowedRoles={['producer', 'admin']}>
                <ProducerTrackRecord user={user} />
              </ProtectedRoute>
            }
          />
          <Route path="/producer/:id" element={<ProducerProfilePublic user={user} />} />
          <Route
            path="/movie/:movieId/crew/manage"
            element={
              <ProtectedRoute allowedRoles={['producer', 'admin']}>
                <MovieCrewManage user={user} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/movie/:id/funding/setup"
            element={
              <ProtectedRoute allowedRoles={['producer', 'admin']}>
                <FundingSetup user={user} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/investor/wallet"
            element={<Navigate to="/investor-dashboard" replace />}
          />
          <Route path="/themes" element={<ThemesIndex />} />
          <Route path="/legal" element={<Legal />} />
          <Route path="/legal/:slug" element={<Legal />} />
          <Route
            path="/invite"
            element={
              <ProtectedRoute>
                <InviteFriends user={user} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <Notifications />
              </ProtectedRoute>
            }
          />
          <Route path="/fraternity" element={<Fraternity user={user} />} />
          <Route path="/fraternity/u/:userId" element={<FraternityProfile user={user} />} />
          <Route
            path="/fraternity/me/edit"
            element={
              <ProtectedRoute>
                <FraternityProfileEdit user={user} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/fraternity/inbox"
            element={
              <ProtectedRoute>
                <FraternityInbox user={user} />
              </ProtectedRoute>
            }
          />
          <Route path="/themes/bollywood" element={<ThemeBollywood user={user} />} />
          <Route path="/themes/hotstar" element={<ThemeHotstar user={user} />} />
          <Route path="/themes/festival" element={<ThemeFestival user={user} />} />
          <Route
            path="/admin-dashboard"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminDashboard user={user} />
              </ProtectedRoute>
            }
          />
        </Routes>
        <VisitorDisclaimerBanner />
        <InstallAppBanner />
        </NativeAppShell>
      </BrowserRouter>
      <Toaster position={isNativeApp() ? 'top-center' : 'top-right'} theme="dark" richColors />
    </div>
    </MotionConfig>
  );
}

export default App;